import React from "react";

// Sparkline - small inline SVG line chart
export const Sparkline = ({ data, color = "#4f74ff", width = 80, height = 26 }) => {
  if (!data || data.length === 0) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data
    .map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`)
    .join(" ");
  const last = data[data.length - 1];
  const first = data[0];
  const up = last >= first;
  const stroke = color === "auto" ? (up ? "#22c55e" : "#ef4444") : color;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

// AreaChart for big equity curve
export const AreaChart = ({ data, height = 220 }) => {
  if (!data || data.length === 0) return null;
  const w = 1000;
  const h = height;
  const min = Math.min(...data) * 0.99;
  const max = Math.max(...data) * 1.01;
  const range = max - min || 1;
  const stepX = w / (data.length - 1);
  const pts = data.map((v, i) => [i * stepX, h - ((v - min) / range) * h]);
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
      <defs>
        <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#4f74ff" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#4f74ff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[0.2, 0.4, 0.6, 0.8].map((g) => (
        <line key={g} x1="0" x2={w} y1={h * g} y2={h * g} stroke="#ffffff10" strokeDasharray="3,4" />
      ))}
      <path d={areaPath} fill="url(#areaGrad)" />
      <path d={linePath} fill="none" stroke="#4f74ff" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// CandleChart - vertical candles for trading terminal
export const CandleChart = ({ candles, height = 380 }) => {
  const w = 1000;
  const h = height;
  const padding = 20;
  const allValues = candles.flatMap((c) => [c.high, c.low]);
  const min = Math.min(...allValues) * 0.999;
  const max = Math.max(...allValues) * 1.001;
  const range = max - min || 1;
  const cw = (w - padding * 2) / candles.length;
  const bodyW = cw * 0.6;
  const y = (v) => padding + ((max - v) / range) * (h - padding * 2);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
      {/* Grid */}
      {[0.2, 0.4, 0.6, 0.8].map((g) => (
        <line key={g} x1="0" x2={w} y1={padding + (h - padding * 2) * g} y2={padding + (h - padding * 2) * g} stroke="#ffffff08" />
      ))}
      {candles.map((c, i) => {
        const cx = padding + i * cw + cw / 2;
        const up = c.close >= c.open;
        const color = up ? "#22c55e" : "#ef4444";
        const yo = y(c.open);
        const yc = y(c.close);
        const yh = y(c.high);
        const yl = y(c.low);
        const bodyY = Math.min(yo, yc);
        const bodyH = Math.max(1, Math.abs(yo - yc));
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={yh} y2={yl} stroke={color} strokeWidth="1" />
            <rect x={cx - bodyW / 2} y={bodyY} width={bodyW} height={bodyH} fill={color} rx="1" />
          </g>
        );
      })}
      {/* Price labels */}
      {[0, 0.25, 0.5, 0.75, 1].map((p) => {
        const val = max - p * range;
        return (
          <text
            key={p}
            x={w - 5}
            y={padding + p * (h - padding * 2) + 4}
            fill="#ffffff55"
            fontSize="10"
            textAnchor="end"
            fontFamily="monospace"
          >
            {val.toFixed(val > 100 ? 0 : 4)}
          </text>
        );
      })}
    </svg>
  );
};

// Generate mock candles
export const generateCandles = (count = 60, start = 1.158, vol = 0.0015) => {
  const out = [];
  let last = start;
  for (let i = 0; i < count; i++) {
    const open = last;
    const drift = (Math.random() - 0.5) * vol * 2;
    const close = Math.max(0.0001, open + drift);
    const wickU = Math.random() * vol;
    const wickD = Math.random() * vol;
    const high = Math.max(open, close) + wickU;
    const low = Math.min(open, close) - wickD;
    out.push({ open, close, high, low });
    last = close;
  }
  return out;
};
